import { useState } from 'react';
import { formatEur, type CashFlowEntry } from '@/lib/cashFlow';
import { prepaymentCandidatesFor, proposedPrepaymentLinkCents } from '@/lib/relatedDocuments';

/**
 * "Seo ettemaksuga" — the manual action that links a prepayment (ETT) to THIS final
 * invoice (ARV), reachable from the shared drill detail (so every lens reaches it,
 * L14). A prepayment offset is only ever applied TO a final invoice, so the action
 * does not exist on any other row. The candidate list and the proposed offset are
 * pure (`prepaymentCandidatesFor` / `proposedPrepaymentLinkCents`); the component
 * computes no money (L9) — the proposed amount is finance-core's outstanding bounded
 * by the prepayment gross, and the link RPC remains the authority on real capacity.
 */
export function LinkPrepaymentPicker({
  final,
  entries,
  onLink,
  isLinking = false,
}: {
  final: CashFlowEntry;
  entries: CashFlowEntry[];
  onLink: (input: { prepaymentEntryId: string; amountCents: number }) => void;
  isLinking?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (final.docType !== 'invoice') return null;
  const candidates = prepaymentCandidatesFor(final, entries);

  return (
    <section className="rounded-lg border border-border bg-card/40">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-[12px] font-semibold text-foreground hover:bg-accent/40"
      >
        <span aria-hidden className="text-[hsl(var(--primary))]">
          +
        </span>
        Seo ettemaksuga
        <span aria-hidden className="ml-auto text-[10px] text-muted-foreground/70">
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <div className="border-t border-border/70 px-3 py-2">
          {candidates.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">Sobivat ettemaksu ei leitud.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {candidates.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={isLinking}
                    onClick={() =>
                      onLink({ prepaymentEntryId: c.id, amountCents: proposedPrepaymentLinkCents(final, c) })
                    }
                    className="flex w-full items-center gap-2 rounded border border-border px-2 py-1 text-[11px] transition-colors hover:border-[hsl(var(--primary))]/50 disabled:opacity-50"
                  >
                    <span className="truncate font-mono text-foreground">{c.docNumber ?? '—'}</span>
                    <span className="truncate text-muted-foreground">{c.party}</span>
                    <span className="ml-auto shrink-0 tabular-nums text-foreground/85">
                      {formatEur(c.money.gross)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
